import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';

import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

import UserManagementTable from './components/UserManagementTable';
import MetricsPanel from './components/MetricsPanel';
import AddUserModal from './components/AddUserModal';

import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import userService from '../../services/userService';
import Icon from '../../components/AppIcon';
import { Activity, Clock, UserPlus } from 'lucide-react';
import realtimeService from '../../services/realtimeService';


export default function AdminDashboard() {
  const { user, userProfile } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState({
    connected: false,
    activeAdmins: [],
    lastActivity: null
  });
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [usersLoading, setUsersLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);

  // Extract artist brand colors — only applied when the logged-in user is an artist
  const isArtist = userProfile?.role === 'artist';
  const highlightColor = isArtist
    ? (userProfile?.highlight_color || userProfile?.highlightColor || null)
    : null;
  const secondaryColor = isArtist
    ? (userProfile?.secondary_color || userProfile?.secondaryColor || highlightColor || null)
    : null;

  // Update current section based on URL query parameters
  useEffect(() => {
    const sectionParam = searchParams?.get('section');
    if (sectionParam) {
      setCurrentSection(sectionParam);
    } else {
      setCurrentSection('dashboard');
    }
  }, [searchParams]);

  // Load users and metrics on component mount
  useEffect(() => {
    const initializeData = async () => {
      if (user) {
        try {
          await Promise.all([loadUsers(), loadMetrics(), loadRecentActivity()]);
        } catch (error) {
          console.error('Error initializing dashboard data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    initializeData();
  }, [user]);

  // Real-time subscriptions setup
  useEffect(() => {
    let unsubscribeActivity;
    let unsubscribeProfiles;
    let unsubscribePresence;

    const setupRealtimeSubscriptions = async () => {
      try {
        // Subscribe to user activity changes
        unsubscribeActivity = realtimeService?.subscribeToUserActivity((newActivity) => {
          setRealtimeStatus(prev => ({
            ...prev,
            lastActivity: {
              user: newActivity?.user_email || 'Unknown',
              action: newActivity?.action_type,
              timestamp: new Date(newActivity.created_at)
            }
          }));

          // Show notification for critical actions
          if (['user_deleted', 'role_changed', 'account_locked']?.includes(newActivity?.action_type)) {
            // You can add a toast notification here
            console.log('Critical activity:', newActivity);
          }
        });

        // Subscribe to user profile changes
        unsubscribeProfiles = realtimeService?.subscribeToUserProfiles((change) => {
          if (change?.event === 'INSERT') {
            // New user added - refresh user list
            loadUsers();
          } else if (change?.event === 'UPDATE') {
            // Update existing user in the list
            setUsers(prevUsers => 
              prevUsers?.map(u => 
                u?.id === change?.profile?.id ? { ...u, ...change?.profile } : u
              )
            );
          } else if (change?.event === 'DELETE') {
            // Remove user from list
            setUsers(prevUsers => prevUsers?.filter(u => u?.id !== change?.profile?.id));
          }
        });

        // Subscribe to admin presence
        if (user?.id) {
          unsubscribePresence = realtimeService?.subscribeToAdminPresence(
            user?.id,
            (activeAdmins) => {
              setRealtimeStatus(prev => ({
                ...prev,
                connected: true,
                activeAdmins: activeAdmins
              }));
            }
          );
        }

      } catch (error) {
        console.error('Error setting up real-time subscriptions:', error);
      }
    };

    setupRealtimeSubscriptions();

    // Cleanup subscriptions on unmount
    return () => {
      if (unsubscribeActivity) unsubscribeActivity();
      if (unsubscribeProfiles) unsubscribeProfiles();
      if (unsubscribePresence) unsubscribePresence();
    };
  }, [user?.id]);

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      setError(null);
      
      const result = await userService?.getUsers({
        pageSize: 50,
        sortBy: 'created_at',
        sortOrder: 'desc'
      });
      
      setUsers(result?.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      setMetricsLoading(true);
      const analytics = await userService?.getPlatformAnalytics();
      
      setMetrics({
        totalUsers: analytics?.totalUsers || 0,
        activeArtists: analytics?.roleCounts?.artist || 0,
        editors: analytics?.roleCounts?.editor || 0,
        admins: analytics?.roleCounts?.admin || 0,
        userGrowth: parseFloat(analytics?.growthRate) || 0,
        artistDistribution: analytics?.totalUsers > 0 
          ? Math.round((analytics?.roleCounts?.artist / analytics?.totalUsers) * 100) 
          : 0,
        editorDistribution: analytics?.totalUsers > 0 
          ? Math.round((analytics?.roleCounts?.editor / analytics?.totalUsers) * 100) 
          : 0,
        adminDistribution: analytics?.totalUsers > 0 
          ? Math.round((analytics?.roleCounts?.admin / analytics?.totalUsers) * 100) 
          : 0
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
      setError('Failed to load metrics.');
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const result = await userService?.getAllUserActivity({ pageSize: 5, sortOrder: 'desc' });
      setRecentActivity(result?.activities || []);
    } catch (error) {
      console.error('Error loading recent activity:', error);
      setRecentActivity([]);
    }
  };

  const handleSearch = (e) => {
    const term = e?.target?.value;
    setSearchTerm(term);
    handleRoleFilter();
  };

  const handleRoleFilter = () => {
    let filtered = users;
    
    if (searchTerm) {
      filtered = filtered?.filter(user => 
        user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }
    
    if (roleFilter !== 'all') {
      filtered = filtered?.filter(user => user?.role === roleFilter);
    }
    
    setFilteredUsers(filtered);
  };

  const handleAddUser = async (userData) => {
    try {
      setError(null); // Clear any previous errors
      console.log('Admin dashboard handling user creation...');
      
      const newUser = await userService?.createUser(userData);
      
      console.log('User created successfully in dashboard:', newUser);
      
      // Show enhanced success message
      const successDetails = [
        `User "${newUser?.full_name || userData?.full_name}" created successfully!`,
        newUser?.email ? `📧 Email: ${newUser?.email}` : '',
        newUser?.role ? `👤 Role: ${newUser?.role?.charAt(0)?.toUpperCase() + newUser?.role?.slice(1)}` : '',
        newUser?.artist_type ? `🎨 Type: ${newUser?.artist_type?.charAt(0)?.toUpperCase() + newUser?.artist_type?.slice(1)}` : '',
        `📊 Status: ${newUser?.status || 'Active'}`,
        '',
        '✅ Next Steps:',
        '• User will receive an email with login instructions',
        '• They must verify their email to access the platform',
        '• You can monitor their status in the user management table'
      ]?.filter(Boolean);
      
      alert(successDetails?.join('\n'));
      
      await loadUsers(); // Reload users list
      await loadMetrics(); // Reload metrics to reflect new user
      setIsAddUserModalOpen(false);
    } catch (error) {
      console.error('Error creating user:', error);
      
      // Enhanced error handling with categorization
      let adminErrorMessage = error?.message || 'Failed to create user. Please try again.';
      
      // Configuration errors
      if (error?.message?.includes('service role') || 
          error?.message?.includes('not properly configured') ||
          error?.message?.includes('configuration')) {
        adminErrorMessage = `❌ CONFIGURATION ISSUE
        
${error?.message}

🔧 ADMINISTRATOR ACTIONS REQUIRED:
1. Check environment variables for SUPABASE_SERVICE_ROLE_KEY
2. Verify Supabase Dashboard → Authentication → Settings
3. Enable "User Management API" in your project
4. Ensure RLS policies allow admin user creation
5. Check project billing status (admin features may require paid plan)

📞 NEXT STEPS:
• Contact your system administrator or DevOps team
• Provide them with this error message
• User creation will be unavailable until configuration is fixed`;
      } 
      // Permission errors
      else if (error?.message?.includes('permission') || 
               error?.message?.includes('unauthorized') ||
               error?.message?.includes('forbidden')) {
        adminErrorMessage = `⚠️ PERMISSION ERROR

${error?.message}

🔐 POSSIBLE CAUSES:
• Your account role is not properly set to "admin" in user_profiles table
• Authentication session has expired or been downgraded
• User management permissions are not configured for your role

✅ SOLUTIONS:
1. Refresh the page and log in again
2. Verify your admin role in the database
3. Contact system administrator to verify your permissions
4. Clear browser cache and cookies, then retry`;
      }
      // Authentication errors  
      else if (error?.message?.includes('Authentication') || 
               error?.message?.includes('session')) {
        adminErrorMessage = `🔑 AUTHENTICATION ERROR

${error?.message}

🔄 IMMEDIATE ACTIONS:
1. Refresh this page to restore your session
2. Log out and log back in as administrator
3. Clear browser cache and cookies
4. Try using an incognito/private browser window

If problem persists, contact your system administrator.`;
      }
      // Network errors
      else if (error?.message?.includes('network') || 
               error?.message?.includes('connection')) {
        adminErrorMessage = `🌐 NETWORK ERROR

${error?.message}

🔧 TROUBLESHOOTING:
1. Check your internet connection
2. Verify Supabase service status
3. Try again in a few minutes
4. Contact technical support if issue persists`;
      }
      
      setError(adminErrorMessage);
      
      // Don't close the modal on error - let user see the error and try again
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const handleEditUser = (user) => {
    console.log('Edit user:', user);
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleEditUserSubmit = async (userData) => {
    try {
      await userService?.updateUser(selectedUser?.id, userData);
      await loadUsers(); // Reload users list
      setIsEditUserModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Failed to update user. Please try again.');
    }
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to delete ${user?.full_name || user?.name}?`)) {
      try {
        setError(null);
        await userService?.deleteUser(user?.id);
        await loadUsers(); // Reload users list
        // Show success message
        alert(`User ${user?.full_name || user?.name} has been deleted successfully.`);
      } catch (error) {
        console.error('Error deleting user:', error);
        setError(`Failed to delete user ${user?.full_name || user?.name}. Please try again.`);
      }
    }
  };

  const handleResendInvitation = async (user) => {
    try {
      setError(null);
      await userService?.resendInvitation(user?.id, user?.email);
      alert(`Invitation resent to ${user?.email}`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError(`Failed to resend invitation to ${user?.email}. Please try again.`);
    }
  };

  const handleBulkAction = async (action, selectedUserIds) => {
    try {
      switch (action) {
        case 'activate':
          await userService?.bulkUpdateUsers(selectedUserIds, { status: 'active' });
          break;
        case 'deactivate':
          await userService?.bulkUpdateUsers(selectedUserIds, { status: 'inactive' });
          break;
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${selectedUserIds?.length} users?`)) {
            const deletePromises = selectedUserIds?.map(userId => 
              userService?.deleteUser(userId)
            );
            await Promise.all(deletePromises);
          } else {
            return;
          }
          break;
        default:
          break;
      }
      
      await loadUsers(); // Reload users list
    } catch (error) {
      console.error('Error performing bulk action:', error);
      setError(`Failed to perform bulk ${action}. Please try again.`);
    }
  };

  const renderSectionContent = () => {
    switch (currentSection) {
      case 'all-users': case'admins': case'editors': case'artists':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-text-primary">
                {currentSection === 'all-users' && 'All Users'}
                {currentSection === 'admins' && 'Admin Users'}
                {currentSection === 'editors' && 'Editor Users'}
                {currentSection === 'artists' && 'Artist Users'}
              </h2>
              <div className="text-sm text-text-secondary">
                {users?.filter(user => currentSection === 'all-users' || user?.role === currentSection?.slice(0, -1))?.length} users
              </div>
            </div>
            <UserManagementTable
              users={users?.filter(user => currentSection === 'all-users' || user?.role === currentSection?.slice(0, -1))}
              loading={usersLoading}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteUser}
              onResendInvitation={handleResendInvitation}
              onBulkAction={handleBulkAction}
              onRefresh={loadUsers}
            />
          </div>
        );
      case 'analytics':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">System Analytics</h2>
              <p className="text-text-secondary mt-1">Comprehensive platform metrics and insights</p>
            </div>
            <MetricsPanel metrics={metrics} loading={metricsLoading} recentActivity={recentActivity} />
            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-lg font-medium text-text-primary mb-4">Advanced Analytics</h3>
              <p className="text-text-secondary">Additional analytics features will be available here.</p>
            </div>
          </div>
        );
      case 'pending-reviews':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Pending Reviews</h2>
              <p className="text-text-secondary mt-1">Content awaiting review and approval</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-6">
              <p className="text-text-secondary">12 items pending review</p>
            </div>
          </div>
        );
      case 'published-content':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Published Content</h2>
              <p className="text-text-secondary mt-1">All approved and published content</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-6">
              <p className="text-text-secondary">Content management interface will be implemented here</p>
            </div>
          </div>
        );
      case 'flagged-content':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Flagged Content</h2>
              <p className="text-text-secondary mt-1">Content that requires immediate attention</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-6">
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 text-xs bg-warning text-warning-foreground rounded-full">3</span>
                <p className="text-text-secondary">items flagged for review</p>
              </div>
            </div>
          </div>
        );
      case 'settings-general':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">General Settings</h2>
              <p className="text-text-secondary mt-1">Platform-wide configuration options</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-6">
              <p className="text-text-secondary">General settings interface will be implemented here</p>
            </div>
          </div>
        );
      case 'settings-security':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Security Settings</h2>
              <p className="text-text-secondary mt-1">Authentication and access control settings</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-6">
              <p className="text-text-secondary">Security settings interface will be implemented here</p>
            </div>
          </div>
        );
      case 'settings-integrations':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Integrations</h2>
              <p className="text-text-secondary mt-1">Third-party service integrations</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-6">
              <p className="text-text-secondary">Integration settings interface will be implemented here</p>
            </div>
          </div>
        );
      default:
        return (
          <>
            {/* Metrics Panel */}
            <MetricsPanel metrics={metrics} loading={metricsLoading} recentActivity={recentActivity} />

            {/* User Management Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-text-primary">User Management</h2>
                <div className="text-sm text-text-secondary">
                  {users?.length} total users
                </div>
              </div>
              
              <UserManagementTable
                users={users}
                loading={usersLoading}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
                onResendInvitation={handleResendInvitation}
                onBulkAction={handleBulkAction}
                onRefresh={loadUsers}
              />
            </div>
          </>
        );
    }
  };

  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Theme Toggle Button - Fixed position like login page */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50 p-3 bg-card hover:bg-accent/20 rounded-full shadow-lg elevation-2 transition-all duration-300 ease-in-out hover:text-accent-foreground"
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? (
          <Sun className="w-5 h-5 text-warning" />
        ) : (
          <Moon className="w-5 h-5 text-text-primary" />
        )}
      </button>

      <Header />

      {/* Artist brand color header accent strip */}
      {highlightColor && (
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(to right, ${highlightColor}, ${secondaryColor || highlightColor}, transparent)` }}
        />
      )}

      <div className="flex">
        <Sidebar onToggleCollapse={() => {}} />
        <main className="flex-1 p-8 ml-64">
          <div className="max-w-7xl mx-auto">
            {/* Real-time Status Bar */}
            <div className="mb-6 bg-card rounded-lg shadow-sm border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${realtimeStatus?.connected ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`}></div>
                    <span className="text-sm font-medium text-text-primary">
                      {realtimeStatus?.connected ? 'Real-time Connected' : 'Real-time Disconnected'}
                    </span>
                  </div>
                  {realtimeStatus?.activeAdmins?.length > 0 && (
                    <div className="flex items-center space-x-2 text-sm text-text-secondary">
                      <Activity size={16} />
                      <span>{realtimeStatus?.activeAdmins?.length} admin{realtimeStatus?.activeAdmins?.length !== 1 ? 's' : ''} online</span>
                    </div>
                  )}
                </div>
                {realtimeStatus?.lastActivity && (
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Clock size={16} />
                    <span>Last activity: {realtimeStatus?.lastActivity?.user} - {realtimeStatus?.lastActivity?.action}</span>
                    <span className="text-muted-foreground">
                      {new Date(realtimeStatus.lastActivity.timestamp)?.toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Page Header */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <div className="flex items-center space-x-3 mb-1">
                  {highlightColor && (
                    <div
                      className="w-1 h-8 rounded-full"
                      style={{ background: `linear-gradient(to bottom, ${highlightColor}, ${secondaryColor || highlightColor})` }}
                    />
                  )}
                  <h1 className="text-3xl font-bold text-text-primary">Admin Dashboard</h1>
                </div>
                <p className="text-text-secondary mt-2">Manage users and monitor system activity</p>
              </div>
              <button
                onClick={() => setIsAddUserModalOpen(true)}
                className="px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
                style={highlightColor ? {
                  background: `linear-gradient(135deg, ${highlightColor}E6, ${secondaryColor || highlightColor}CC)`,
                  color: 'white',
                  boxShadow: `0 2px 8px ${highlightColor}40`
                } : {}}
                {...(!highlightColor ? { className: 'bg-primary hover:bg-accent text-primary-foreground px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 hover:text-accent-foreground' } : {})}
              >
                <UserPlus size={20} />
                <span>Add New User</span>
              </button>
            </div>

            {/* Enhanced Error Message Display */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" className="text-error mt-1 flex-shrink-0" size={20} />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-error font-medium flex items-center gap-2">
                      System Error
                      <span className="text-xs bg-error/10 text-error px-2 py-1 rounded-full">
                        Admin Dashboard
                      </span>
                    </h4>
                    <div className="text-error/90 mt-2 text-sm">
                      <pre className="whitespace-pre-wrap font-mono text-xs bg-error/5 p-3 rounded border border-error/10 overflow-x-auto">
{error}
                      </pre>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button 
                        onClick={() => setError(null)}
                        className="px-3 py-1 bg-error/10 hover:bg-error/20 text-error rounded text-sm font-medium transition-colors"
                      >
                        Dismiss Error
                      </button>
                      <button 
                        onClick={() => {
                          setError(null);
                          loadUsers();
                          loadMetrics();
                        }}
                        className="px-3 py-1 bg-error/10 hover:bg-error/20 text-error rounded text-sm font-medium transition-colors"
                      >
                        Retry Operations
                      </button>
                      <button 
                        onClick={() => window.location?.reload()}
                        className="px-3 py-1 bg-error/10 hover:bg-error/20 text-error rounded text-sm font-medium transition-colors"
                      >
                        Refresh Page
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dynamic Section Content */}
            {renderSectionContent()}
          </div>
        </main>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onAddUser={handleAddUser}
        editMode={false}
        title="Add New User"
      />

      {/* Edit User Modal */}
      <AddUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => { setIsEditUserModalOpen(false); setSelectedUser(null); }}
        onAddUser={handleEditUserSubmit}
        editMode={true}
        initialData={selectedUser}
        title="Edit User"
      />
    </div>
  );
}